// Labels, colours and helpers shared by the Keys tab's Fobs list, Audit log and fob detail.
import moment from "moment";
import type { StatusColor } from "shared/utils/statusChipColor";
import type { PhysicalKeyExternalSync } from "./siteKeysApi";

// ---------------------------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------------------------

/** A key date in the format the Keys tab has always used, or "—". */
export function formatKeyDate(value: string | null | undefined): string {
  return value ? moment(value).format("YYYY-MM-DD h:mm A") : "—";
}

/** A fob's validity as one value: "from – to", "From …", "Until …" or "—". */
export function formatValidity(
  validFrom: string | null | undefined,
  validTo: string | null | undefined,
): string {
  if (validFrom && validTo) return `${formatKeyDate(validFrom)} – ${formatKeyDate(validTo)}`;
  if (validFrom) return `From ${formatKeyDate(validFrom)}`;
  if (validTo) return `Until ${formatKeyDate(validTo)}`;
  return "—";
}

/**
 * Parses a UTC timestamp from the API. The external sync record's timestamps are UTC but are
 * serialised without an offset ("2026-09-30T10:00:00"), which `new Date` would read as local
 * time; a value with no "Z" or offset is treated as UTC.
 */
export function parseUtcTimestamp(value: string): Date {
  const hasOffset = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim());
  return new Date(hasOffset ? value : `${value.trim()}Z`);
}

/** An external sync timestamp (UTC) in the browser's local time, or `fallback`. */
export function formatUtcTimestamp(
  value: string | null | undefined,
  fallback = "Never",
): string {
  return value ? parseUtcTimestamp(value).toLocaleString() : fallback;
}

// ---------------------------------------------------------------------------------------------
// External sync (a fob's credential on a sister site)
// ---------------------------------------------------------------------------------------------

/**
 * How bad each external sync status is, for picking the one to show when a fob syncs to more than
 * one sister site. Failed needs action; Skipped means the fob isn't eligible; Unassigned and
 * Removed are normal states for an unassigned or revoked fob. Unknown (newer) statuses rank just
 * above Synced so they aren't hidden behind it.
 */
const EXTERNAL_SYNC_STATUS_RANK: Record<string, number> = {
  Synced: 0,
  Removed: 2,
  Unassigned: 3,
  Skipped: 4,
  Failed: 5,
};
const UNKNOWN_STATUS_RANK = 1;

export function externalSyncStatusRank(status: string): number {
  return EXTERNAL_SYNC_STATUS_RANK[status] ?? UNKNOWN_STATUS_RANK;
}

/** The entry with the worst status (the first one wins ties), or undefined when there are none. */
export function worstExternalSync(
  syncs: readonly PhysicalKeyExternalSync[] | null | undefined,
): PhysicalKeyExternalSync | undefined {
  if (!syncs || syncs.length === 0) return undefined;
  return syncs.reduce((worst, sync) =>
    externalSyncStatusRank(sync.status) > externalSyncStatusRank(worst.status)
      ? sync
      : worst,
  );
}

export function externalSyncStatusColor(status: string): StatusColor {
  switch (status) {
    case "Synced":
      return "success";
    case "Skipped":
      return "warning";
    case "Failed":
      return "error";
    default:
      // Unassigned, Removed and anything newer.
      return "default";
  }
}

/** "Brivo Tower" or, if the site name isn't known, the system name. */
export function externalSyncSiteLabel(sync: PhysicalKeyExternalSync): string {
  return sync.destinationSiteName || `${sync.externalSystemType} site`;
}

// ---------------------------------------------------------------------------------------------
// Audit events
// ---------------------------------------------------------------------------------------------

/** Every ConnectApi PhysicalKeyEvent value, in the enum's order. */
export const PHYSICAL_KEY_EVENTS = [
  "Scanned",
  "Created",
  "Encoded",
  "AssignedUser",
  "ReassignUser",
  "UnassignedUser",
  "AssignedGroup",
  "UnassignedGroup",
  "Revoked",
  "Unrevoke",
  "Deleted",
  "AddedToRelatedSite",
  "AssignedToRelatedSiteUser",
  "UnassignedFromRelatedSiteUser",
  "RelatedSiteSyncFailed",
  "RemovedFromRelatedSite",
] as const;

export type PhysicalKeyEvent = (typeof PHYSICAL_KEY_EVENTS)[number];

/**
 * Friendly label and badge colour for each event. Related-site events say "sister site" rather
 * than naming a system, because audit rows don't say which system the sister site runs.
 */
export const PHYSICAL_KEY_EVENT_INFO: Record<
  PhysicalKeyEvent,
  { label: string; color: StatusColor }
> = {
  Scanned: { label: "Scanned", color: "default" },
  Created: { label: "Enrolled", color: "info" },
  Encoded: { label: "Encoded", color: "info" },
  AssignedUser: { label: "Assigned", color: "success" },
  ReassignUser: { label: "Reassigned", color: "success" },
  UnassignedUser: { label: "Unassigned", color: "warning" },
  AssignedGroup: { label: "Assigned to key group", color: "success" },
  UnassignedGroup: { label: "Unassigned from key group", color: "warning" },
  Revoked: { label: "Revoked", color: "error" },
  Unrevoke: { label: "Unrevoked", color: "success" },
  Deleted: { label: "Deleted", color: "error" },
  AddedToRelatedSite: { label: "Added to sister site", color: "info" },
  AssignedToRelatedSiteUser: { label: "Assigned on sister site", color: "success" },
  UnassignedFromRelatedSiteUser: { label: "Unassigned on sister site", color: "warning" },
  RelatedSiteSyncFailed: { label: "Sister site sync failed", color: "error" },
  RemovedFromRelatedSite: { label: "Removed from sister site", color: "default" },
};

function isKnownEvent(event: string): event is PhysicalKeyEvent {
  return Object.prototype.hasOwnProperty.call(PHYSICAL_KEY_EVENT_INFO, event);
}

/** The friendly label, or the raw name for an event this UI doesn't know yet. */
export function physicalKeyEventLabel(event: string): string {
  return isKnownEvent(event) ? PHYSICAL_KEY_EVENT_INFO[event].label : event;
}

export function physicalKeyEventColor(event: string): StatusColor {
  return isKnownEvent(event) ? PHYSICAL_KEY_EVENT_INFO[event].color : "default";
}

/** Events whose old/new value is a guest (linked to the guest page). */
export const GUEST_EVENTS: ReadonlySet<string> = new Set<PhysicalKeyEvent>([
  "AssignedUser",
  "ReassignUser",
  "UnassignedUser",
]);

/**
 * Events whose old (unassigned, removed) or new (added, assigned, failed) value is the sister
 * site, linked to that site's page.
 */
export const RELATED_SITE_EVENTS: ReadonlySet<string> = new Set<PhysicalKeyEvent>([
  "AddedToRelatedSite",
  "AssignedToRelatedSiteUser",
  "UnassignedFromRelatedSiteUser",
  "RelatedSiteSyncFailed",
  "RemovedFromRelatedSite",
]);
